// Placeholder service layer for the Learning Hub.
// Swap the arrays for API calls (Coursera, edX, Udemy, Duolingo, Credly) when integrations land.

export interface Course {
  id: string;
  title: string;
  provider: string;
  level: "beginner" | "intermediate" | "advanced";
  hours: number;
  price: "free" | "paid";
  skills: string[];
  rating: number;
  url?: string;
}

export interface LearningPath {
  id: string;
  title: string;
  role: string;
  months: number;
  steps: { title: string; hours: number; skills: string[] }[];
}

export interface LanguageResource {
  id: string;
  language: string;
  name: string;
  type: "app" | "course" | "podcast" | "exam";
  level: string;
  free: boolean;
  url?: string;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  earnedOn: string;
  expiresOn?: string;
  credentialId?: string;
  skills: string[];
}

export interface ScholarshipPrepTask {
  id: string;
  title: string;
  category: "documents" | "essay" | "test" | "references" | "financial";
  done: boolean;
}

export const COURSES: Course[] = [
  {
    id: "c1",
    title: "Modern Full-Stack Web Development",
    provider: "Coursera",
    level: "intermediate",
    hours: 60,
    price: "paid",
    skills: ["React", "Node.js", "SQL"],
    rating: 4.7,
  },
  {
    id: "c2",
    title: "Data Analytics with Python",
    provider: "edX",
    level: "beginner",
    hours: 40,
    price: "free",
    skills: ["Python", "Pandas", "Statistics"],
    rating: 4.6,
  },
  {
    id: "c3",
    title: "IELTS Academic Preparation",
    provider: "British Council",
    level: "intermediate",
    hours: 30,
    price: "paid",
    skills: ["English", "Writing", "Speaking"],
    rating: 4.8,
  },
  {
    id: "c4",
    title: "Project Management Foundations",
    provider: "Google",
    level: "beginner",
    hours: 24,
    price: "free",
    skills: ["Agile", "Scrum", "Leadership"],
    rating: 4.7,
  },
  {
    id: "c5",
    title: "Cloud Architect Path (AWS)",
    provider: "AWS Skill Builder",
    level: "advanced",
    hours: 80,
    price: "paid",
    skills: ["AWS", "Networking", "Security"],
    rating: 4.5,
  },
  {
    id: "c6",
    title: "German A1–A2 Fast Track",
    provider: "Goethe-Institut",
    level: "beginner",
    hours: 45,
    price: "paid",
    skills: ["German", "Grammar"],
    rating: 4.4,
  },
  {
    id: "c7",
    title: "Registered Nurse Bridging (UK NMC)",
    provider: "OpenLearn",
    level: "advanced",
    hours: 55,
    price: "paid",
    skills: ["Nursing", "NMC", "Clinical"],
    rating: 4.6,
  },
  {
    id: "c8",
    title: "Digital Marketing Fundamentals",
    provider: "HubSpot Academy",
    level: "beginner",
    hours: 20,
    price: "free",
    skills: ["SEO", "Content", "Analytics"],
    rating: 4.5,
  },
];

export const LEARNING_PATHS: LearningPath[] = [
  {
    id: "p1",
    title: "Junior Software Engineer",
    role: "Software Engineer",
    months: 6,
    steps: [
      { title: "Web fundamentals", hours: 30, skills: ["HTML", "CSS", "JavaScript"] },
      { title: "React & TypeScript", hours: 60, skills: ["React", "TypeScript"] },
      { title: "APIs & databases", hours: 40, skills: ["Node.js", "SQL"] },
      { title: "Portfolio & interviews", hours: 30, skills: ["Git", "System Design"] },
    ],
  },
  {
    id: "p2",
    title: "Data Analyst Ready",
    role: "Data Analyst",
    months: 4,
    steps: [
      { title: "Spreadsheets & SQL", hours: 25, skills: ["Excel", "SQL"] },
      { title: "Python for analysis", hours: 40, skills: ["Python", "Pandas"] },
      { title: "Dashboards", hours: 20, skills: ["Power BI", "Tableau"] },
      { title: "Capstone project", hours: 25, skills: ["Storytelling"] },
    ],
  },
  {
    id: "p3",
    title: "Nurse Migration Track",
    role: "Registered Nurse (UK/Ireland)",
    months: 8,
    steps: [
      { title: "IELTS/OET preparation", hours: 60, skills: ["English"] },
      { title: "CBT theory exam", hours: 80, skills: ["NMC CBT"] },
      { title: "OSCE practical prep", hours: 60, skills: ["OSCE"] },
      { title: "Registration & visa", hours: 20, skills: ["NMC", "Health & Care Visa"] },
    ],
  },
];

export const LANGUAGE_RESOURCES: LanguageResource[] = [
  {
    id: "l1",
    language: "English",
    name: "IELTS Free Practice",
    type: "exam",
    level: "B1–C1",
    free: true,
  },
  {
    id: "l2",
    language: "English",
    name: "BBC Learning English",
    type: "podcast",
    level: "A2–C1",
    free: true,
  },
  {
    id: "l3",
    language: "German",
    name: "Deutsche Welle Nicos Weg",
    type: "course",
    level: "A1–B1",
    free: true,
  },
  {
    id: "l4",
    language: "French",
    name: "TV5Monde Apprendre",
    type: "course",
    level: "A1–C1",
    free: true,
  },
  { id: "l5", language: "Arabic", name: "Rocket Arabic", type: "app", level: "A1–B2", free: false },
  { id: "l6", language: "Japanese", name: "JLPT N5 Prep", type: "exam", level: "N5", free: false },
  { id: "l7", language: "Spanish", name: "Duolingo", type: "app", level: "A1–B2", free: true },
];

const CERTS_KEY = "goh.learning.certs";
const SCHOLAR_KEY = "goh.learning.scholarship.prep";

function readLS<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    return JSON.parse(window.localStorage.getItem(key) ?? "null") ?? fallback;
  } catch {
    return fallback;
  }
}
function writeLS<T>(key: string, value: T) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function listCertifications(): Certification[] {
  return readLS<Certification[]>(CERTS_KEY, []);
}
export function addCertification(c: Omit<Certification, "id">): Certification {
  const cert = { ...c, id: crypto.randomUUID() };
  writeLS(CERTS_KEY, [cert, ...listCertifications()]);
  return cert;
}
export function removeCertification(id: string) {
  writeLS(
    CERTS_KEY,
    listCertifications().filter((c) => c.id !== id),
  );
}

const DEFAULT_SCHOLARSHIP_TASKS: ScholarshipPrepTask[] = [
  { id: "s1", title: "Academic transcripts (certified)", category: "documents", done: false },
  { id: "s2", title: "Passport valid 12+ months", category: "documents", done: false },
  { id: "s3", title: "Statement of purpose draft", category: "essay", done: false },
  { id: "s4", title: "Personal essay (500–800 words)", category: "essay", done: false },
  { id: "s5", title: "IELTS / TOEFL score", category: "test", done: false },
  { id: "s6", title: "GRE / GMAT (if required)", category: "test", done: false },
  { id: "s7", title: "Two academic references", category: "references", done: false },
  { id: "s8", title: "One professional reference", category: "references", done: false },
  { id: "s9", title: "Proof of funds / bank statement", category: "financial", done: false },
  { id: "s10", title: "Budget plan for study year", category: "financial", done: false },
];

export function listScholarshipTasks(): ScholarshipPrepTask[] {
  return readLS<ScholarshipPrepTask[]>(SCHOLAR_KEY, DEFAULT_SCHOLARSHIP_TASKS);
}
export function toggleScholarshipTask(id: string) {
  const list = listScholarshipTasks().map((t) => (t.id === id ? { ...t, done: !t.done } : t));
  writeLS(SCHOLAR_KEY, list);
  return list;
}

// Simple career skill matcher: score a user's skills against a target role.
export interface SkillMatch {
  role: string;
  requiredSkills: string[];
  matched: string[];
  missing: string[];
  score: number;
  suggestedCourses: Course[];
}

export const ROLE_SKILLS: Record<string, string[]> = {
  "Software Engineer": ["JavaScript", "React", "Node.js", "SQL", "Git"],
  "Data Analyst": ["Excel", "SQL", "Python", "Pandas", "Tableau"],
  "Registered Nurse (UK)": ["Nursing", "NMC", "English", "Clinical"],
  "Digital Marketer": ["SEO", "Content", "Analytics", "Ads"],
  "Cloud Engineer": ["AWS", "Networking", "Security", "Linux"],
  "Project Manager": ["Agile", "Scrum", "Leadership", "Budgeting"],
};

export function matchCareerSkills(userSkills: string[], role: string): SkillMatch {
  const required = ROLE_SKILLS[role] ?? [];
  const norm = (s: string) => s.trim().toLowerCase();
  const userSet = new Set(userSkills.map(norm));
  const matched = required.filter((s) => userSet.has(norm(s)));
  const missing = required.filter((s) => !userSet.has(norm(s)));
  const score = required.length ? Math.round((matched.length / required.length) * 100) : 0;
  const missingNorm = new Set(missing.map(norm));
  const suggestedCourses = COURSES.filter((c) =>
    c.skills.some((s) => missingNorm.has(norm(s))),
  ).slice(0, 4);
  return { role, requiredSkills: required, matched, missing, score, suggestedCourses };
}

export function recommendCourses(interests: string[]): Course[] {
  if (interests.length === 0) return COURSES.slice(0, 4);
  const norm = interests.map((i) => i.trim().toLowerCase());
  return COURSES.map((c) => ({
    c,
    hits: c.skills.filter((s) =>
      norm.some((n) => s.toLowerCase().includes(n) || n.includes(s.toLowerCase())),
    ).length,
  }))
    .filter((x) => x.hits > 0)
    .sort((a, b) => b.hits - a.hits || b.c.rating - a.c.rating)
    .map((x) => x.c);
}
